import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { FontSizeSwitcherPlugin } from '@doltech/ui/lib/figma/DolEditor/decorators/font-size-switcher/FontSizeSwitcherPlugin';
import { FontSizeModeEnum } from '@doltech/ui/lib/figma/DolEditor/decorators/font-size-switcher/types';
import { DolEditor } from '@doltech/ui/lib/figma/DolEditor/DolEditor';
import { DEFAULT_HIGHLIGHT_COLORS_LIST } from '@doltech/ui/lib/figma/DolEditor/elements/highlight/defaults';
import { HighlightElementPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/highlight/plugin';
import { MarkToScrollPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/mark-to-scroll/plugin';
import { MarkId } from '@doltech/ui/lib/figma/DolEditor/elements/mark-to-scroll/renderElement';
import { ReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/handlers/read-only/ReadOnlyPlugin';
import { SlateListPlugin } from '@doltech/ui/lib/figma/DolEditor/marks/list';
import { ReadingText } from '@doltech/ui/lib/figma/Typography';
import { BoldPlugin } from '@doltech/ui/lib/slatePlugins/marks/bold/BoldPlugin';
import { withInlineVoid } from '@doltech/ui/lib/slatePlugins/plugins/inline-void/withInlineVoid';
import { ExplanationTypeEnum } from '@doltech/utils/lib/constants';
import cl from 'classnames';
import { isNil, minBy, sumBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useDebounce } from 'react-use';
import styled from 'styled-components';

import { useTestResultStore } from '../model/test-result.hook';

const Main = styled.section`
  padding: 0 !important;
  .paragraph-container {
    display: flex;
    flex-direction: column;
    position: relative;
    padding: 16px 0;
  }

  .paragraph-header-subtitle {
    margin-top: 24px;
  }
  .dol-editor {
    pointer-events: none;
  }
  ${fromScreen(768)} {
    .dol-editor {
      pointer-events: auto;
    }
  }
  .paragraph-header {
    padding: 0 24px;
    position: relative;
  }
  .paragraph-content {
    padding: 0 24px;
    position: relative;
    padding-bottom: 200px;
  }

  .highlight-overlay {
    &-header,
    &-top,
    &-bottom {
      display: none;
    }
  }

  &.show-overlay {
    .highlight-overlay {
      &-header,
      &-top,
      &-bottom {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1;
        background-color: rgba(0, 0, 0, 0.2);
      }

      &-header {
        top: -24px;
        bottom: 0;
      }

      &-bottom {
        bottom: 0;
      }
    }
  }
`;
export interface PassageParagraphProps extends React.HTMLAttributes<HTMLDivElement> {}

const highlightPassage = ({ explanation, setHighlightExplanationLocator, isLocateExplanation }) => {
  let offsetTop = 0;
  let offsetHeight = 0;
  let scrollTop = 0;
  const sidebar = document.getElementById('sidebar-left');
  if (explanation.type === ExplanationTypeEnum.LOCATING_DETAIL) {
    const allParagraphElms = Array.from(document.getElementsByClassName('paragraph')).filter(
      (elm) => {
        return Boolean(elm?.querySelector('[color]') || elm?.querySelector(`#${MarkId}`));
      }
    );
    const minElm = minBy(allParagraphElms, 'offsetTop') as HTMLElement;
    offsetTop = minElm?.offsetTop || 0;
    offsetHeight = sumBy(allParagraphElms, 'offsetHeight');
    scrollTop = offsetTop - 32;
  } else {
    const markerElement = document.getElementById(MarkId);
    const parentElement = markerElement ? (markerElement.parentNode as HTMLElement) : null;
    if (parentElement) {
      offsetTop = parentElement.offsetTop;
      offsetHeight = parentElement.offsetHeight;
      scrollTop = offsetTop;
    }
  }

  if (offsetHeight > 0) {
    setHighlightExplanationLocator({
      top: Math.max(offsetTop, 24),
      height: offsetHeight,
    });
    if (sidebar && isLocateExplanation) {
      sidebar.scrollTop = scrollTop;
    }
  }
};

export const PassageParagraph = observer((props: PassageParagraphProps) => {
  const { className } = props;
  const store = useTestResultStore();

  const currentSection = store?.getCurrentTestSection();
  const { passage } = currentSection;
  const currentQuestion = store.getCurrentQuestionObject();
  const no = currentQuestion?.no || 0;
  const isLocateExplanation = currentQuestion?.isLocateExplanationEnabled;
  const [editorId, setEditorId] = React.useState<string>(
    `${no}${isLocateExplanation}${store.currentQuestionIndex}`
  );

  React.useEffect(() => {
    setEditorId(`${no}${isLocateExplanation}${store.currentQuestionIndex}`);
  }, [isLocateExplanation, no, store.currentQuestionIndex]);

  const getExplanation = React.useCallback(() => {
    const question = store.questions.find(
      (_question, cQuestionIndex) => cQuestionIndex === store.currentQuestionIndex
    );
    return question?.correction?.explanation || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, editorId]);

  const [highlightExplanationLocator, setHighlightExplanationLocator] = React.useState({
    top: 0,
    height: 0,
  });

  const editorValue = React.useMemo(() => {
    return currentSection.getPassageParagraph({
      explanation: getExplanation(),
      isLocateExplanation,
    });
  }, [currentSection, getExplanation, isLocateExplanation]);

  useDebounce(
    () => {
      highlightPassage({
        setHighlightExplanationLocator,
        explanation: getExplanation(),
        isLocateExplanation,
      });
    },
    100,
    [editorId]
  );

  const isShowOverlay = React.useMemo(() => {
    return isLocateExplanation && highlightExplanationLocator.height > 0;
  }, [highlightExplanationLocator.height, isLocateExplanation]);

  const plugins = React.useMemo(
    () => [
      ReadOnlyPlugin(),
      MarkToScrollPlugin(),
      BoldPlugin({ bold: { hotkey: null } }),
      HighlightElementPlugin({
        colors: DEFAULT_HIGHLIGHT_COLORS_LIST,
      }),
      FontSizeSwitcherPlugin(FontSizeModeEnum.DEFAULT),
    ],
    []
  );
  const withPlugins = React.useMemo(
    () => [SlateListPlugin.withPlugin(), withInlineVoid({ plugins })],
    [plugins]
  );

  if (!store.initialed || isNil(currentSection)) return null;

  return (
    <Main className={cl(className, { 'show-overlay': isShowOverlay })} {...props}>
      {/* <pre>{JSON.stringify(editorValue, null, 2)}</pre> */}
      <div className="paragraph-container">
        <div className="paragraph-header">
          <div className="highlight-overlay-header" />
          <div className="paragraph-header-title">
            <ReadingText style={{ fontSize: 24, fontWeight: 700 }}>{passage.title}</ReadingText>
          </div>
          {passage?.subTitle && (
            <div className="paragraph-header-subtitle">
              <ReadingText style={{ fontSize: 16, fontWeight: 600 }}>
                {passage.subTitle}
              </ReadingText>
            </div>
          )}
        </div>
        <div className="paragraph-content">
          <div
            style={{
              height: highlightExplanationLocator.top - 24,
            }}
            className="highlight-overlay-top"
          />
          <div
            style={{
              top: highlightExplanationLocator.top + highlightExplanationLocator.height + 24,
            }}
            className="highlight-overlay-bottom"
          />
          <DolEditor
            key={editorId}
            initialValue={editorValue}
            plugins={plugins}
            editorChains={withPlugins}
          />
        </div>
      </div>
    </Main>
  );
});
